// src/features/intervene/hooks/useChatPanel.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  interveneMessageListAtom,
  interveneUserDetailAtom,
  interveneFilterAtom,
  interveneUserListAtom,
  interveneChatCountsAtom,
} from '../state/interveneAtoms';
import { userInformationAtom } from '../../../Services/_states';
import { interveneService } from '../services/interveneService';
import { appendMessageUpload, getUploadMessageType } from '../utils/getUploadMessageType';
import { validateAttachmentFile } from '../utils/attachmentConstraints';
import { parseInterveneApiError } from '../utils/parseInterveneApiError';
import { buildSessionTogglePayload } from '../utils/sessionPayload';
import {
  canEndInterveneSession,
  canStartInterveneSession,
  isInterveneSessionActive,
} from '../utils/sessionToggleRules';
import { chatDebug, summarizeMessages } from '../utils/chatDebug';
import { normalizeMessagesResponse } from '../utils/interveneApiAdapters';
import { applySocketListUpdate } from '../sockets/handlers/handleListUpdated';
import { normalizeListPatchFromSocket } from '../sockets/handlers/normalizeListPatch';
import { INTERVENE_MESSAGE_PAGE_SIZE, INTERVENE_USER_TABS } from '../constants/interveneApi';
import {
  adjustChatCountsForInterveneEnd,
  adjustChatCountsForInterveneStart,
} from '../utils/adjustChatCountsForTabMove';
import { normalizeChatUser } from '../utils/interveneApiAdapters';
import { isDuplicateInterveneMessage } from '../utils/messageDedup';
import SweetAlert from '../../../components/Shared/Notification';
import { getInterveneCompanyId } from '../utils/interveneOrganisationScope';
import { checkPhoneOptedOut } from '../../whatsapp-templates/services/optoutService';

function buildRequestFilters({ phoneNumber, botNumber, page, organisationId }) {
  return {
    phone_number: phoneNumber,
    botNumber,
    page,
    pageLimit: INTERVENE_MESSAGE_PAGE_SIZE,
    organisationId,
  };
}

function getChatKey(phoneNumber, botNumber) {
  return phoneNumber && botNumber ? `${phoneNumber}:${botNumber}` : '';
}

function normalizeConversationMessages(messages, filters) {
  return (messages || []).map((message) => ({
    ...message,
    phone_number: message?.phone_number || filters.phone_number,
    bot_number: message?.bot_number || filters.botNumber,
  }));
}

export default function useChatPanel({
  switchToTabPreservingChat,
  refreshChatCounts,
} = {}) {
  const [activeUser, setActiveUser] = useRecoilState(interveneUserDetailAtom);
  const globalFilters = useRecoilValue(interveneFilterAtom);
  const userInfo = useRecoilValue(userInformationAtom);

  const [messageState, setMessageState] = useRecoilState(interveneMessageListAtom);
  const setUserList = useSetRecoilState(interveneUserListAtom);
  const userList = useRecoilValue(interveneUserListAtom);
  const setChatCounts = useSetRecoilState(interveneChatCountsAtom);
  const [draftMessage, setDraftMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const isPaginationFetchingRef = useRef(false);
  const selectionVersionRef = useRef(0);
  const activeChatKeyRef = useRef('');
  const previousActiveChatKeyRef = useRef('');
  const loadedPagesRef = useRef(new Set([1]));
  const shouldStickToBottomRef = useRef(true);
  const previewUrlsRef = useRef(new Map());

  const agentName = userInfo?.username || userInfo?.first_name || 'You';
  const scopedOrganisationId = getInterveneCompanyId(globalFilters, userInfo);
  const activeChatKey = getChatKey(activeUser?.phone_number, globalFilters.botNumber);

  if (previousActiveChatKeyRef.current !== activeChatKey) {
    chatDebug('active-chat-key-change', {
      previousKey: previousActiveChatKeyRef.current,
      nextKey: activeChatKey,
      activeUser: {
        phone_number: activeUser?.phone_number,
        whatsapp_name: activeUser?.whatsapp_name,
      },
      botNumber: globalFilters.botNumber,
      nextSelectionVersion: selectionVersionRef.current + 1,
    });
    previousActiveChatKeyRef.current = activeChatKey;
    activeChatKeyRef.current = activeChatKey;
    selectionVersionRef.current += 1;
    isPaginationFetchingRef.current = false;
    loadedPagesRef.current = new Set([1]);
    shouldStickToBottomRef.current = true;
  }

  const hasMoreMessages =
    (messageState.messages?.length || 0) < (messageState.total_count || 0);

  const loadMessages = useCallback(
    async (filters, { reset = false, isOlderPage = false } = {}) => {
      if (isOlderPage && isPaginationFetchingRef.current) return;

      if (isOlderPage) {
        isPaginationFetchingRef.current = true;
      }

      const requestVersion = selectionVersionRef.current;
      const requestChatKey = getChatKey(filters.phone_number, filters.botNumber);

      chatDebug('messages-request-start', {
        requestVersion,
        requestChatKey,
        activeChatKey: activeChatKeyRef.current,
        page: filters.page,
        reset,
        isOlderPage,
        filters,
      });

      if (isOlderPage) setIsLoadingOlder(true);
      else setIsLoadingMessages(true);

      try {
        const res = await interveneService.getUserMessages(filters);
        const isStaleRequest = requestVersion !== selectionVersionRef.current;
        const isDifferentChat = requestChatKey !== activeChatKeyRef.current;
        if (isStaleRequest || isDifferentChat) {
          chatDebug(
            'messages-response-rejected-before-apply',
            {
              reason: isStaleRequest ? 'stale-selection-version' : 'different-active-chat',
              requestVersion,
              currentSelectionVersion: selectionVersionRef.current,
              requestChatKey,
              activeChatKey: activeChatKeyRef.current,
              page: filters.page,
              status: res?.status,
            },
            'warn'
          );
          return;
        }

        if (res.status === 200) {
          const { messages: pageMessages, totalCount } = normalizeMessagesResponse(res);
          const sortedData = normalizeConversationMessages(
            pageMessages.reverse(),
            filters
          );

          chatDebug('messages-response-received', {
            requestVersion,
            requestChatKey,
            page: filters.page,
            reset,
            isOlderPage,
            receivedCount: sortedData.length,
            totalCount,
            sample: summarizeMessages(sortedData),
          });

          setMessageState((prev) => {
            const prevChatKey = getChatKey(prev.filters?.phone_number, prev.filters?.botNumber);
            const prevHasChat = Boolean(prevChatKey);

            if (prevHasChat && prevChatKey !== requestChatKey) {
              chatDebug(
                'messages-state-apply-rejected',
                {
                  reason: 'atom-chat-key-mismatch',
                  prevChatKey,
                  requestChatKey,
                  prevFilters: prev.filters,
                  requestFilters: filters,
                  page: filters.page,
                  sample: summarizeMessages(sortedData),
                },
                'warn'
              );
              return prev;
            }

            const nextMessages = reset ? sortedData : [...sortedData, ...(prev.messages || [])];
            chatDebug('messages-state-apply', {
              prevChatKey,
              requestChatKey,
              page: filters.page,
              reset,
              isOlderPage,
              prevCount: prev.messages?.length || 0,
              incomingCount: sortedData.length,
              nextCount: nextMessages.length,
              totalCount,
              nextFilters: {
                ...prev.filters,
                ...filters,
              },
              sample: summarizeMessages(nextMessages),
            });

            return {
              messages: nextMessages,
              total_count: totalCount,
              filters: {
                ...prev.filters,
                ...filters,
              },
            };
          });

          if (reset) {
            loadedPagesRef.current = new Set([filters.page || 1]);
            shouldStickToBottomRef.current = true;
          } else if (filters.page) {
            loadedPagesRef.current.add(filters.page);
            shouldStickToBottomRef.current = false;
          }
        }
      } catch (err) {
        const isCurrentSelection = requestVersion === selectionVersionRef.current;
        const isCurrentChat = requestChatKey === activeChatKeyRef.current;
        if (!isCurrentSelection || !isCurrentChat) {
          chatDebug(
            'messages-request-error-ignored',
            {
              requestVersion,
              currentSelectionVersion: selectionVersionRef.current,
              requestChatKey,
              activeChatKey: activeChatKeyRef.current,
              error: err?.message,
            },
            'warn'
          );
          return;
        }
        chatDebug(
          'messages-request-error',
          {
            requestVersion,
            requestChatKey,
            page: filters.page,
            error: err?.message,
          },
          'error'
        );
        SweetAlert(err.message || 'Error loading messages', 'error');
      } finally {
        const isCurrentSelection = requestVersion === selectionVersionRef.current;
        const isCurrentChat = requestChatKey === activeChatKeyRef.current;
        if (isOlderPage) {
          setIsLoadingOlder(false);
          isPaginationFetchingRef.current = false;
        } else if (isCurrentSelection && isCurrentChat) {
          setIsLoadingMessages(false);
        }
      }
    },
    [setMessageState]
  );

  const loadMessagesRef = useRef(loadMessages);
  loadMessagesRef.current = loadMessages;

  useEffect(() => {
    if (!activeUser?.phone_number || !globalFilters.botNumber) {
      chatDebug('active-chat-cleared', {
        activeUserPhone: activeUser?.phone_number,
        botNumber: globalFilters.botNumber,
      });
      activeChatKeyRef.current = '';
      setIsLoadingMessages(false);
      setIsLoadingOlder(false);
      isPaginationFetchingRef.current = false;
      return;
    }

    loadedPagesRef.current = new Set([1]);
    shouldStickToBottomRef.current = true;
    chatDebug('initial-load-triggered', {
      activeChatKey: getChatKey(activeUser.phone_number, globalFilters.botNumber),
      selectionKey: activeUser?.selection_key,
      phoneNumber: activeUser.phone_number,
      botNumber: globalFilters.botNumber,
    });

    loadMessagesRef.current(
      buildRequestFilters({
        phoneNumber: activeUser.phone_number,
        botNumber: globalFilters.botNumber,
        page: 1,
        organisationId: scopedOrganisationId,
      }),
      { reset: true }
    );
  }, [
    activeUser?.phone_number,
    activeUser?.selection_key,
    globalFilters.botNumber,
    scopedOrganisationId,
  ]);

  const loadOlderMessages = useCallback(async () => {
    if (
      !activeUser?.phone_number ||
      !globalFilters.botNumber ||
      !hasMoreMessages ||
      isPaginationFetchingRef.current
    ) {
      chatDebug('older-load-blocked', {
        reason: !activeUser?.phone_number
          ? 'missing-active-user'
          : !globalFilters.botNumber
            ? 'missing-bot-number'
            : !hasMoreMessages
              ? 'no-more-messages'
              : 'pagination-in-flight',
        activeUserPhone: activeUser?.phone_number,
        botNumber: globalFilters.botNumber,
        hasMoreMessages,
        isPaginationFetching: isPaginationFetchingRef.current,
        messageFilters: messageState.filters,
      });
      return;
    }

    const currentPage = messageState.filters?.page || 1;
    const nextPage = currentPage + 1;
    const stateChatKey = getChatKey(messageState.filters?.phone_number, messageState.filters?.botNumber);
    const currentActiveChatKey = getChatKey(activeUser.phone_number, globalFilters.botNumber);

    if (stateChatKey !== currentActiveChatKey) {
      chatDebug(
        'older-load-blocked',
        {
          reason: 'state-active-chat-mismatch',
          stateChatKey,
          currentActiveChatKey,
          messageFilters: messageState.filters,
          activeUserPhone: activeUser.phone_number,
          botNumber: globalFilters.botNumber,
        },
        'warn'
      );
      return;
    }

    if (loadedPagesRef.current.has(nextPage)) {
      chatDebug('older-load-blocked', {
        reason: 'page-already-loaded',
        nextPage,
        loadedPages: Array.from(loadedPagesRef.current),
        stateChatKey,
      });
      return;
    }

    chatDebug('older-load-triggered', {
      stateChatKey,
      currentActiveChatKey,
      currentPage,
      nextPage,
      currentCount: messageState.messages?.length || 0,
      totalCount: messageState.total_count,
    });

    await loadMessages(
      buildRequestFilters({
        phoneNumber: activeUser.phone_number,
        botNumber: globalFilters.botNumber,
        page: nextPage,
        organisationId: scopedOrganisationId,
      }),
      { isOlderPage: true }
    );
  }, [
    activeUser?.phone_number,
    globalFilters.botNumber,
    scopedOrganisationId,
    hasMoreMessages,
    loadMessages,
    messageState.filters?.page,
    messageState.filters?.phone_number,
    messageState.filters?.botNumber,
  ]);

  const handleSendMessage = async (fileObj = null) => {
    if ((!draftMessage.trim() && !fileObj) || !activeUser?.phone_number) return;

    if (await checkPhoneOptedOut(activeUser.phone_number)) {
      SweetAlert('Cannot send message: contact has opted out of WhatsApp messages', 'warning');
      return;
    }

    if (fileObj) {
      const uploadType = getUploadMessageType(fileObj);
      const expectedKind =
        uploadType === 'file'
          ? 'document'
          : uploadType === 'image' || uploadType === 'video' || uploadType === 'audio'
            ? uploadType
            : null;
      const validation = validateAttachmentFile(fileObj, expectedKind);
      if (!validation.valid) {
        SweetAlert(validation.message, 'error');
        return;
      }
    }

    setIsSending(true);
    shouldStickToBottomRef.current = true;

    const sendChatKey = getChatKey(activeUser.phone_number, globalFilters.botNumber);
    const pendingText = draftMessage.trim();
    const optimisticId = `local-${Date.now()}`;
    let previewUrl = null;

    const formData = new FormData();
    formData.append('phone_number', activeUser.phone_number);

    if (fileObj) {
      appendMessageUpload(formData, fileObj, pendingText);
    } else {
      formData.append('type', 'text');
      formData.append('text', pendingText);
    }

    setDraftMessage('');

    const optimisticMessage = {
      msg_id: optimisticId,
      created_at: new Date().toISOString(),
      timestamp: Date.now(),
      origin: 'OUTBOUND',
      phone_number: activeUser.phone_number,
      chat_type: 'human_agent',
      intervened_by: agentName,
      status: { type: 'new message sent' },
    };

    if (fileObj) {
      previewUrl = URL.createObjectURL(fileObj);
      previewUrlsRef.current.set(optimisticId, previewUrl);
      const uploadType = formData.get('type');
      optimisticMessage.content = {
        type: uploadType === 'file' ? 'document' : uploadType,
        text: pendingText,
        filename: fileObj.name,
        url: previewUrl,
        previewUrl: uploadType === 'image' ? previewUrl : undefined,
      };
    } else {
      optimisticMessage.content = { type: 'text', text: pendingText, body: { text: pendingText } };
    }

    setMessageState((prev) => {
      const prevChatKey = getChatKey(prev.filters?.phone_number, prev.filters?.botNumber);
      if (prevChatKey && prevChatKey !== sendChatKey) return prev;

      return {
        ...prev,
        messages: [...(prev.messages || []), optimisticMessage],
        total_count: (prev.total_count || 0) + 1,
      };
    });

    try {
      const res = await interveneService.sendMessage(formData, globalFilters.botNumber);
      if (!res?.data?.status) {
        throw new Error(parseInterveneApiError(res, 'Failed to send message'));
      }

      const serverMessage =
        res?.data?.data?.message ||
        res?.data?.data ||
        res?.data?.message ||
        null;
      const serverId =
        serverMessage?.w_id ||
        serverMessage?.msg_id ||
        res?.data?.data?.w_id ||
        res?.data?.w_id ||
        null;

      if (fileObj && !serverId) {
        throw new Error(parseInterveneApiError(res, 'Media was not delivered to WhatsApp.'));
      }

      if (serverId) {
        setMessageState((prev) => {
          const prevChatKey = getChatKey(prev.filters?.phone_number, prev.filters?.botNumber);
          if (prevChatKey !== sendChatKey) return prev;

          return {
            ...prev,
            messages: (prev.messages || []).map((message) =>
              message.msg_id === optimisticId
                ? {
                    ...message,
                    ...serverMessage,
                    msg_id: serverId,
                    w_id: serverId,
                    status: serverMessage?.status || message.status,
                  }
                : message
            ),
          };
        });
      }

      const storedPreview = previewUrlsRef.current.get(optimisticId);
      if (storedPreview) {
        URL.revokeObjectURL(storedPreview);
        previewUrlsRef.current.delete(optimisticId);
      }
    } catch (err) {
      setMessageState((prev) => {
        const prevChatKey = getChatKey(prev.filters?.phone_number, prev.filters?.botNumber);
        if (prevChatKey !== sendChatKey) return prev;

        return {
          ...prev,
          messages: (prev.messages || []).filter((message) => message.msg_id !== optimisticId),
          total_count: Math.max(0, (prev.total_count || 1) - 1),
        };
      });
      const storedPreview = previewUrlsRef.current.get(optimisticId);
      if (storedPreview) {
        URL.revokeObjectURL(storedPreview);
        previewUrlsRef.current.delete(optimisticId);
      }
      if (activeChatKeyRef.current === sendChatKey) {
        setDraftMessage(pendingText);
        SweetAlert(err.message || 'Error sending message', 'error');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleTemplateSent = useCallback(
    (message) => {
      if (!message?.phone_number || !globalFilters.botNumber) return;

      const sendChatKey = getChatKey(message.phone_number, globalFilters.botNumber);
      shouldStickToBottomRef.current = true;

      setMessageState((prev) => {
        const prevChatKey = getChatKey(prev.filters?.phone_number, prev.filters?.botNumber);
        if (prevChatKey !== sendChatKey) return prev;

        const messages = prev.messages || [];
        if (messages.some((existing) => isDuplicateInterveneMessage(existing, message))) {
          return prev;
        }

        const [normalizedMessage] = normalizeConversationMessages([message], {
          phone_number: message.phone_number,
          botNumber: globalFilters.botNumber,
        });

        return {
          ...prev,
          messages: [...messages, normalizedMessage],
          total_count: (prev.total_count || 0) + 1,
        };
      });
    },
    [globalFilters.botNumber, setMessageState]
  );

  const toggleSession = async () => {
    if (!activeUser?.phone_number) return;

    const isOngoing = isInterveneSessionActive(activeUser);
    const userType = globalFilters?.userType;

    if (isOngoing && !canEndInterveneSession(userType, activeUser)) return;
    if (!isOngoing && !canStartInterveneSession(userType, activeUser)) return;

    if (!isOngoing && (await checkPhoneOptedOut(activeUser.phone_number))) {
      SweetAlert('Cannot start intervene: contact has opted out of WhatsApp messages', 'warning');
      return;
    }

    try {
      const payload = buildSessionTogglePayload({
        phoneNumber: activeUser.phone_number,
        botNumber: globalFilters.botNumber,
        organisationId: scopedOrganisationId,
        isOngoing,
      });

      const res = await interveneService.toggleSessionStatus(payload);
      if (res.status === 200) {
        const nextIntervene = !isOngoing;
        const currentUserId = userInfo?.id ?? null;
        const currentUsername = userInfo?.username || userInfo?.first_name || agentName;
        setActiveUser((prev) => ({
          ...prev,
          intervene: nextIntervene,
          session_start: nextIntervene ? new Date().toISOString() : null,
          intervened_by: nextIntervene ? currentUsername : null,
          intervened_by_user_id: nextIntervene ? currentUserId : null,
        }));

        const sessionPatch = normalizeListPatchFromSocket(
          {
            phone_number: activeUser.phone_number,
            bot_number: globalFilters.botNumber,
            whatsapp_name: activeUser.whatsapp_name,
            intervene: nextIntervene,
            session_start: nextIntervene ? new Date().toISOString() : null,
            updated_at: new Date().toISOString(),
            intervened_at: new Date().toISOString(),
            last_active_date: activeUser.last_active_date,
            last_message: activeUser.last_message,
            last_message_preview: activeUser.last_message_preview,
            intervened_by: nextIntervene ? currentUsername : null,
            intervened_by_user_id: nextIntervene ? currentUserId : null,
          },
          { existingUser: activeUser, isActiveChat: true }
        );

        if (nextIntervene) {
          const userWasInList = (userList.users || []).some(
            (user) => `${user.phone_number}` === `${activeUser.phone_number}`
          );

          setChatCounts((prev) =>
            adjustChatCountsForInterveneStart(prev, userType, {
              userWasVisible: userWasInList,
            })
          );

          const switchedTab = userType !== INTERVENE_USER_TABS.INTERVENE
            ? switchToTabPreservingChat?.(INTERVENE_USER_TABS.INTERVENE)
            : false;

          const targetTab = INTERVENE_USER_TABS.INTERVENE;
          const intervenedUser = normalizeChatUser({
            ...activeUser,
            ...sessionPatch,
            bot_number: globalFilters.botNumber,
          });

          if (switchedTab) {
            setUserList({
              users: [intervenedUser],
              total_count: 1,
              has_next: false,
            });
          } else {
            setUserList((prev) => {
              const existingUser = (prev.users || []).find(
                (user) => `${user.phone_number}` === `${activeUser.phone_number}`
              );
              return applySocketListUpdate(prev, {
                patch: sessionPatch,
                tabValue: targetTab,
                existingUser,
              });
            });
          }
        } else {
          setChatCounts((prev) => adjustChatCountsForInterveneEnd(prev));
          setUserList((prev) => {
            const existingUser = (prev.users || []).find(
              (user) => `${user.phone_number}` === `${activeUser.phone_number}`
            );
            return applySocketListUpdate(prev, {
              patch: sessionPatch,
              tabValue: userType,
              existingUser,
            });
          });
          refreshChatCounts?.(globalFilters.botNumber, scopedOrganisationId);
        }

        SweetAlert(isOngoing ? 'Intervene ended' : 'Intervene started', 'success');
      }
    } catch (err) {
      const extractMessage = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return val;
        if (Array.isArray(val)) return extractMessage(val[0]);
        if (typeof val === 'object') {
          const firstKey = Object.keys(val)[0];
          return firstKey ? extractMessage(val[firstKey]) : null;
        }
        return String(val);
      };

      const errorData = err?.response?.data;
      const apiErrorMessage =
        extractMessage(errorData?.data?.general_error) ||
        extractMessage(errorData?.general_error) ||
        extractMessage(errorData?.message) ||
        extractMessage(errorData?.error) ||
        extractMessage(errorData?.detail) ||
        extractMessage(errorData?.data?.message) ||
        extractMessage(errorData?.data) ||
        extractMessage(errorData) ||
        err?.message ||
        'Error toggling session';

      SweetAlert(apiErrorMessage, 'error');
    }
  };

  return {
    activeUser,
    messages: messageState.messages,
    isLoadingMessages,
    isLoadingOlder,
    hasMoreMessages,
    draftMessage,
    setDraftMessage,
    isSending,
    loadOlderMessages,
    shouldStickToBottomRef,
    handleSendMessage,
    handleTemplateSent,
    toggleSession,
  };
}
